// Implements verification recovery workflows through the Supabase Edge Function boundary.
import { supabase } from '@/integrations/supabase/client';
import type { VerificationRecoveryPort, VerificationRecoveryResult } from '../core/types';
import {
  buildAuthCallbackUrl,
  extractFunctionError,
  normalizeStructuredEdgeFunctionErrorPayload,
  normalizeVerificationRecoveryResult,
  parseFunctionPayload,
  pickResponseHeaders,
} from './authFunctionSupport';

function buildStructuredVerificationRecoveryDebug(
  structuredError: ReturnType<typeof normalizeStructuredEdgeFunctionErrorPayload>,
  dataError: unknown,
) {
  if (!structuredError) {
    return undefined;
  }

  return {
    stage: structuredError.stage,
    message: structuredError.message,
    errorType: 'StructuredEdgeFunctionError',
    code: structuredError.code,
    details: structuredError.details ?? undefined,
    hint: structuredError.hint ?? null,
    status: structuredError.status ?? null,
    serializedCause: dataError,
    trace: [],
    timestamp: new Date().toISOString(),
  };
}

export function createSupabaseVerificationRecoveryPort(): VerificationRecoveryPort {
  return {
    async getVerificationRecoveryStatus(input) {
      const { data, error, response } = await supabase.functions.invoke('request-verification-email', {
        body: {
          email: input.email,
          mode: 'status',
        },
      });

      if (error) {
        const payload = await parseFunctionPayload(response);
        return {
          error: await extractFunctionError(error, response),
          email: input.email,
          canResend: true,
          cooldownSecondsRemaining: 0,
          nextAllowedAt: null,
          lastSentAt: null,
          debug: payload?.debug as VerificationRecoveryResult['debug'],
          httpStatus: response?.status ?? null,
          responseHeaders: pickResponseHeaders(response),
        };
      }

      if (data?.error) {
        const structuredError = normalizeStructuredEdgeFunctionErrorPayload(data.error);
        return {
          error: new Error(structuredError?.message || String(data.error)),
          email: input.email,
          canResend: true,
          cooldownSecondsRemaining: 0,
          nextAllowedAt: null,
          lastSentAt: null,
          debug: buildStructuredVerificationRecoveryDebug(structuredError, data.error),
        };
      }

      return normalizeVerificationRecoveryResult(data, input.email);
    },

    async requestVerificationEmail(input) {
      const { data, error, response } = await supabase.functions.invoke('request-verification-email', {
        body: {
          email: input.email,
          redirectTo: input.redirectTo ?? buildAuthCallbackUrl(),
          mode: 'resend',
        },
      });

      if (error) {
        const payload = await parseFunctionPayload(response);
        const debug = payload?.debug as VerificationRecoveryResult['debug'];
        return {
          error: await extractFunctionError(error, response),
          email: input.email,
          canResend: true,
          cooldownSecondsRemaining: 0,
          nextAllowedAt: null,
          lastSentAt: null,
          debug: debug ?? {
            stage: 'client_transport',
            message: error.message,
            errorType: error.name,
            trace: [],
            email: input.email,
            redirectTo: input.redirectTo ?? buildAuthCallbackUrl(),
            timestamp: new Date().toISOString(),
          },
          httpStatus: response?.status ?? null,
          responseHeaders: pickResponseHeaders(response),
        };
      }

      if (data?.error) {
        const structuredError = normalizeStructuredEdgeFunctionErrorPayload(data.error);
        return {
          error: new Error(structuredError?.message || String(data.error)),
          email: input.email,
          canResend: data?.canResend ?? true,
          cooldownSecondsRemaining: data?.cooldownSecondsRemaining ?? 0,
          nextAllowedAt: data?.nextAllowedAt ?? null,
          lastSentAt: data?.lastSentAt ?? null,
          debug: buildStructuredVerificationRecoveryDebug(structuredError, data.error) ?? data?.debug,
          httpStatus: response?.status ?? null,
          responseHeaders: pickResponseHeaders(response),
        };
      }

      return normalizeVerificationRecoveryResult({
        ...data,
        httpStatus: response?.status ?? null,
        responseHeaders: pickResponseHeaders(response),
      }, input.email);
    },
  };
}
